const API_URL = import.meta.env.VITE_API_URL || '/api';

export interface Tool {
  id: number;
  title: string;
  description?: string;
  icon: string;
  url: string;
  color: string;
}

export interface AdminTool extends Tool {
  isActive: boolean;
  order: number;
  userIds: number[];
}

export interface User {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  isAdmin: boolean;
  isActive?: boolean;
  tools?: number[];
}

export interface NewsItem {
  title: string;
  url: string;
  source: string;
  publishedAt: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = {
  auth: {
    login: async (email: string, password: string): Promise<LoginResponse> => {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new ApiError(response.status, data.message || 'Erreur de connexion');
      }

      return data;
    },

    me: async (token: string): Promise<{ user: User }> => {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new ApiError(response.status, 'Session expirée');
      }

      return response.json();
    },
  },

  tools: {
    getAll: async (token: string): Promise<{ tools: Tool[] }> => {
      const response = await fetch(`${API_URL}/tools`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new ApiError(response.status, 'Erreur lors du chargement des outils');
      }

      return response.json();
    },

    getAllAdmin: async (token: string): Promise<{ tools: AdminTool[] }> => {
      const response = await fetch(`${API_URL}/tools/admin`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new ApiError(response.status, 'Erreur lors du chargement des outils');
      }

      return response.json();
    },

    create: async (token: string, data: {
      title: string;
      description?: string;
      icon: string;
      url: string;
      color: string;
      order?: number;
    }): Promise<{ tool: Tool }> => {
      const response = await fetch(`${API_URL}/tools`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new ApiError(response.status, responseData.message || 'Erreur lors de la création');
      }

      return responseData;
    },

    update: async (token: string, id: number, data: {
      title?: string;
      description?: string;
      icon?: string;
      url?: string;
      color?: string;
      order?: number;
    }): Promise<{ tool: Tool }> => {
      const response = await fetch(`${API_URL}/tools/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new ApiError(response.status, responseData.message || 'Erreur lors de la mise à jour');
      }

      return responseData;
    },

    delete: async (token: string, id: number): Promise<void> => {
      const response = await fetch(`${API_URL}/tools/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const responseData = await response.json();
        throw new ApiError(response.status, responseData.message || 'Erreur lors de la suppression');
      }
    },

    updatePermissions: async (token: string, id: number, userIds: number[]): Promise<void> => {
      const response = await fetch(`${API_URL}/tools/${id}/permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ userIds }),
      });

      if (!response.ok) {
        const responseData = await response.json();
        throw new ApiError(response.status, responseData.message || 'Erreur lors de la mise à jour des permissions');
      }
    },
  },

  news: {
    getAll: async (token: string): Promise<{ items: NewsItem[] }> => {
      const response = await fetch(`${API_URL}/news`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new ApiError(response.status, 'Erreur lors du chargement des actualités');
      }

      return response.json();
    },
  },

  users: {
    getAll: async (token: string): Promise<{ users: User[] }> => {
      const response = await fetch(`${API_URL}/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new ApiError(response.status, 'Erreur lors du chargement des utilisateurs');
      }

      return response.json();
    },

    getTools: async (token: string): Promise<{ tools: Tool[] }> => {
      const response = await fetch(`${API_URL}/users/tools`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new ApiError(response.status, 'Erreur lors du chargement des outils');
      }

      return response.json();
    },

    create: async (token: string, data: {
      email: string;
      password: string;
      firstName?: string;
      lastName?: string;
      isAdmin?: boolean;
      tools?: number[];
    }): Promise<{ user: User }> => {
      const response = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new ApiError(response.status, responseData.message || 'Erreur lors de la création');
      }

      return responseData;
    },

    update: async (token: string, id: number, data: {
      email?: string;
      password?: string;
      firstName?: string;
      lastName?: string;
      isAdmin?: boolean;
      tools?: number[];
    }): Promise<{ user: User }> => {
      const response = await fetch(`${API_URL}/users/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new ApiError(response.status, responseData.message || 'Erreur lors de la mise à jour');
      }

      return responseData;
    },

    delete: async (token: string, id: number): Promise<void> => {
      const response = await fetch(`${API_URL}/users/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const responseData = await response.json();
        throw new ApiError(response.status, responseData.message || 'Erreur lors de la suppression');
      }
    },
  },
};

export { API_URL };
